/**
 * The Meta-pixel marketing block a checkout reports (feature 0044 lot B).
 *
 * Lives HERE, beside `attribution.ts`, for the same reason it does: two submit sites in two different apps
 * (the one-page COD buy box, shipped from this package, and the storefront's classic checkout form) must
 * compose the exact same block, and duplicating it would let them drift.
 *
 * Same three constraints as attribution, word for word:
 *  1. **Never throws.** The checkout imports it — a shopper must never lose an order because a cookie read
 *     hit a disabled storage API. Every failure degrades to "we do not know".
 *  2. **Content, not identity.** The server re-validates every value against the exact shapes the Meta
 *     pixel produces and DROPS what does not conform; nothing here can choose the tenant, the pixel or the
 *     money. We still send the block on every checkout: the row it becomes server-side is ALSO the "this
 *     order came from the storefront" marker the Conversions-API send gates on.
 *  3. **Zero dependencies.** Browser APIs only.
 *
 * One deliberate difference from attribution: `aqx_attr` keeps the NAME of a click id and drops its value;
 * this block carries the `fbclid` VALUE (and the `_fbp`/`_fbc` cookies Meta's own script wrote). That is
 * its entire purpose — the Conversions API cannot attribute a sale to the ad click without the click's
 * identifier, and the case that matters MOST is precisely the one where the browser pixel was blocked and
 * `_fbc` never existed. Scope stays tight: the value lives in `sessionStorage` (tab lifetime, like
 * `aqx_attr`), travels only inside the checkout POST body, and the server drops anything malformed.
 */
const FBCLID_KEY = 'aqx_fbclid';
const MAX_TOKEN = 300;
/** The seller's own "ignore my traffic" cookie (spec §5.8) — set via the dashboard's link, read here. */
export const OWN_TRAFFIC_COOKIE = 'aqx-own-traffic';
/**
 * Captures the landing `fbclid` VALUE once per tab (the `aqx_attr` write-once discipline: the landing is
 * what the LANDING said). Idempotent and safe to call from every page — the beacon init calls it, and the
 * pixel script calls it again in case the beacon was blocked but the pixel was not.
 */
export function captureFbclid(doc) {
    try {
        // Resolved INSIDE the try: a default parameter `= document` would throw a ReferenceError in any
        // non-browser runtime (SSR import, node tests) BEFORE the body's guards could catch it.
        const d = doc ?? document;
        if (window.sessionStorage.getItem(FBCLID_KEY))
            return;
        const raw = new URL(d.location.href).searchParams.get('fbclid');
        const value = raw?.trim();
        if (!value || value.length > MAX_TOKEN)
            return;
        window.sessionStorage.setItem(FBCLID_KEY, value);
    }
    catch {
        // Storage denied / URL hostile — "we do not know" is a valid answer, never a thrown one.
    }
}
/** The composed block for a checkout submit, or undefined when the browser told us nothing at all. */
export function marketingPayload(doc) {
    try {
        // Same non-browser guard as captureFbclid: resolve `document` inside the try, never as a default.
        const d = doc ?? document;
        const payload = {};
        const fbp = readCookie(d, '_fbp');
        if (fbp)
            payload.fbp = fbp;
        const fbc = readCookie(d, '_fbc');
        if (fbc)
            payload.fbc = fbc;
        try {
            const fbclid = window.sessionStorage.getItem(FBCLID_KEY)?.trim();
            if (fbclid)
                payload.fbclid = fbclid;
        }
        catch {
            // sessionStorage denied — the cookies above may still carry the attribution.
        }
        // Origin + path only, NEVER the query string — it can carry a token, a customer id or an email, and
        // the server strips it again regardless (belt and braces on a value that ends up in a Meta call).
        payload.sourceUrl = d.location.origin + d.location.pathname;
        // The cookie only counts while the seller's « Ignorer mon propre trafic » setting is ON — the
        // EXACT rule the pixel tag applies (meta-pixel.ts): unchecking the setting must neutralise the
        // cookie EVERYWHERE, orders included, or the seller sees browser events flow while their own
        // test orders silently never reach the server channel (founder, 2026-08-15).
        if (readCookie(d, OWN_TRAFFIC_COOKIE) === '1' && ownTrafficHonored(d))
            payload.ownTraffic = true;
        return payload;
    }
    catch {
        return undefined;
    }
}
/**
 * Whether the page's pixel config still honours the own-traffic cookie — `true` unless the
 * `aqx-pixels` meta EXPLICITLY carries `excludeOwnTraffic: false` (same `!== false` reading as the
 * pixel tag, so both surfaces flip together). Absent or unreadable meta keeps the cookie honoured:
 * failing OPEN here could only ever suppress a send wrongly, never fire one.
 */
function ownTrafficHonored(doc) {
    try {
        const raw = doc.querySelector('meta[name="aqx-pixels"]')?.getAttribute('content');
        if (!raw)
            return true;
        const parsed = JSON.parse(raw);
        return parsed.config?.excludeOwnTraffic !== false;
    }
    catch {
        return true;
    }
}
/** `document.cookie` scan, bounded and guarded — a cookie jar is ordinary hostile input. */
function readCookie(doc, name) {
    try {
        const prefix = `${name}=`;
        for (const part of doc.cookie.split(';')) {
            const trimmed = part.trim();
            if (trimmed.startsWith(prefix)) {
                const value = decodeURIComponent(trimmed.slice(prefix.length)).trim();
                return value && value.length <= MAX_TOKEN ? value : undefined;
            }
        }
        return undefined;
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=marketing.js.map